import { PolymorphicComponentPropWithRef } from "@omni/types/components/utils/polymorphic.types";
import { TransitionPropTypeKeys } from "@omni/ui/components/Fade/fade.props";
import { noop } from "@omni/utils/function.utils";
import { isDefined } from "@omni/utils/helper.utils";
import { omit, pick } from "@omni/utils/object.utils";
import { cn } from "@omni/utils/styles";
import React, { useRef, useState } from "react";
import { Transition } from "react-transition-group";
import {
    TransitionProps,
    TransitionStatus
} from "react-transition-group/Transition";

const TransitionStatuses = {
    ENTERING: "entering",
    ENTERED: "entered",
    EXITING: "exiting",
    EXITED: "exited"
};

const transitionStatusToClassHash = {
    [TransitionStatuses.ENTERING]: "collapsing",
    [TransitionStatuses.ENTERED]: "collapse show",
    [TransitionStatuses.EXITING]: "collapsing",
    [TransitionStatuses.EXITED]: "collapse"
};

function getTransitionClass(status: TransitionStatus) {
    return transitionStatusToClassHash[status] || "collapse";
}

function getHeight(node: HTMLElement) {
    return node.scrollHeight;
}

export type CollapseComponentProps = Partial<TransitionProps> & {
    isOpen?: boolean;
    navbar?: boolean;
    addEndListener?: (done: () => void) => void;
};

export type CollapseProps<T extends React.ElementType = "div"> =
    PolymorphicComponentPropWithRef<T, CollapseComponentProps, "div">;

const Collapse = <T extends React.ElementType = "div">({
    ref,
    onEnter: _onEnter = noop,
    onEntering = noop,
    onEntered = noop,
    onExit = noop,
    onExited = noop,
    onExiting = noop,
    children,
    className,
    isOpen = false,
    appear: _appear = false,
    enter: _enter = true,
    exit: _exit = true,
    navbar,
    tag,
    timeout: _timeout = 150,
    addEndListener = noop,
    ...otherProps
}: CollapseProps<T>) => {
    const nodeRef = useRef<HTMLElement | null>(null);

    const mergedRef = (node: HTMLElement) => {
        nodeRef.current = node;

        if (!ref) return;
        if (typeof ref === "function") {
            ref(node);
        } else {
            ref.current = node;
        }
    };

    const [height, setHeight] = useState<null | number>();

    const Tag = tag || ("div" as React.ElementType);

    const onInnerEntering = (isAppearing: boolean) => {
        const node = nodeRef.current;
        if (!isDefined(node)) return;

        setHeight(getHeight(node));
        onEntering(node, isAppearing);
    };

    const onInnerEntered = (isAppearing: boolean) => {
        const node = nodeRef.current;
        if (!isDefined(node)) return;

        setHeight(null);
        onEntered(node, isAppearing);
    };

    const onInnerExit = () => {
        const node = nodeRef.current;
        if (!isDefined(node)) return;

        setHeight(getHeight(node));
        onExit(node);
    };

    const onInnerExiting = () => {
        const node = nodeRef.current;
        if (!isDefined(node)) return;

        // getting this variable triggers a reflow
        const _unused = node.offsetHeight;
        setHeight(0);
        onExiting(node);
    };

    const onInnerExited = () => {
        const node = nodeRef.current;
        if (!isDefined(node)) return;

        setHeight(null);
        onExited(node);
    };

    const transitionProps = pick(otherProps, TransitionPropTypeKeys);
    const childProps = omit(otherProps, TransitionPropTypeKeys);

    return (
        <Transition
            mountOnEnter={false}
            unmountOnExit={false}
            addEndListener={addEndListener}
            {...transitionProps}
            in={isOpen}
            onEntering={onInnerEntering}
            onEntered={onInnerEntered}
            onExit={onInnerExit}
            onExiting={onInnerExiting}
            onExited={onInnerExited}
            nodeRef={nodeRef}
        >
            {(status) => {
                const collapseClass = getTransitionClass(status);
                const classes = cn(
                    collapseClass,
                    navbar && "navbar-collapse",
                    className
                );
                const style = height === null ? null : { height };
                return (
                    <Tag
                        {...childProps}
                        style={{ ...otherProps.style, ...style }}
                        className={classes}
                        ref={mergedRef}
                    >
                        {children}
                    </Tag>
                );
            }}
        </Transition>
    );
};

export { Collapse };
